import * as React from 'react';
import { ChangeEvent, useMemo } from 'react';
import { InputGroup, FormControl } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { IOfferItems, IOfferItemsRequest } from '../../../model/offer/IOfferItems';
import { checkNumberPosts, checkPrice, getPriceOneContent } from '../../functions/Functions';
import { TrashIcon } from '../../Icons';
import { CustomImageHandler } from '../CustomImageHandler';
import Counter from '../Counter';
import { priceSplitter } from '../../../utils/helpers/priceSplitter';

interface SocialPlatformOfferItemProps {
    item: IOfferItems;
    initialOfferItems: IOfferItemsRequest[];
    isCharity: boolean;
    offerModalStatus: 'negotiate' | 'preview' | string;
    expended: boolean;
    deleteOfferItem: (socialPlatformId: number) => void;
    changeHandlerOfferItem: (
        socialPlatformId: number,
        event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        nbPosts?: number
    ) => void;
    isInfluencerOrAgency?: boolean;
    isCharityOrPrivate?: boolean;
}

const SocialPlatformOfferItem: React.FC<SocialPlatformOfferItemProps> = ({
    item,
    initialOfferItems,
    isCharity,
    offerModalStatus,
    expended,
    deleteOfferItem,
    changeHandlerOfferItem,
    isCharityOrPrivate,
    isInfluencerOrAgency = true,
}) => {
    const { t } = useTranslation();

    if (!item || !item.socialPlatform || !item.socialPlatform.id) {
        return null;
    }

    const { id: socialId, name, path } = item.socialPlatform;
    const defaultPosts = item.nbPosts ?? 0;

    const price = useMemo(
        () => checkPrice(socialId, item.price ?? 0, initialOfferItems ?? []),
        [socialId, item.price, initialOfferItems]
    );

    const postNumber = useMemo(
        () => checkNumberPosts(socialId, defaultPosts, initialOfferItems ?? []),
        [socialId, defaultPosts, initialOfferItems]
    );

    const unitPrice = useMemo(
        () => getPriceOneContent(initialOfferItems ?? [], socialId),
        [initialOfferItems, socialId]
    );

    const rowClass = `position-relative ${
        postNumber === 0 || (!item.id && offerModalStatus === 'preview') ? 'text-line-through' : ''
    } ${expended ? '' : 'd-none'}`;

    const handlePriceChange = (event: ChangeEvent<HTMLInputElement>) => {
        const next = Number(event.target.value);
        if (Number.isFinite(next) && next >= 0) {
            changeHandlerOfferItem(socialId, event, undefined);
        } else if (event.target.value === '') {
            changeHandlerOfferItem(socialId, event, undefined);
        }
    };

    return (
        <tr key={item.id} className={rowClass}>
            <td colSpan={2}>
                <div className="d-flex align-items-center pl-2">
                    {offerModalStatus === 'negotiate' && (
                        <button
                            type="button"
                            className="btn p-0 mb-0"
                            aria-label={t('general.remove') || 'Remove'}
                            onClick={() => deleteOfferItem(socialId)}
                        >
                            <TrashIcon width="15" height="16" classes="cursor-pointer mr-2" />
                        </button>
                    )}
                    <CustomImageHandler
                        classes={isInfluencerOrAgency ? 'd-none d-md-flex' : ''}
                        altTag={name ?? ''}
                        photoPath={path ?? ''}
                    />
                    <span className="px-2 mb-0">{t(`general.platforms.${name}`)}</span>
                </div>
            </td>

            <td className="mb-0 delete-margin-bottom">
                <div className="mr-3">
                    {offerModalStatus === 'negotiate' ? (
                        <Counter
                            defaultCount={postNumber}
                            setNumberItems={changeHandlerOfferItem}
                            id={socialId}
                            itemId={socialId}
                        />
                    ) : (
                        <span className="text-muted font-weight-bold mb-0">x {item.nbPosts ?? 0}</span>
                    )}
                </div>
            </td>

            <td
                className={`${
                    isCharity ? 'd-none' : `${isInfluencerOrAgency && 'd-none d-md-table-cell text-center'}`
                }`}
            >
                <div className="text-muted text-center">
                    <p>{t('influencer.offer_management.modal.price_of_one_content')}</p>
                    <div className="euro-symbol">{unitPrice}</div>
                </div>
            </td>

            <td className={`${isCharity ? 'd-none' : `${isInfluencerOrAgency && 'd-none d-md-table-cell'}`}`}></td>

            <td className={`${isCharity ? 'd-none' : 'd-flex justify-content-end'}`}>
                {offerModalStatus === 'negotiate' ? (
                    <InputGroup id="price-input">
                        <InputGroup.Text>€</InputGroup.Text>
                        <FormControl
                            min={0}
                            onChange={handlePriceChange}
                            type="number"
                            value={price === 0 ? '' : price}
                            placeholder="0"
                            disabled={postNumber < 1}
                            required={isCharityOrPrivate ?? isCharity ? false : postNumber > 0}
                            name="price"
                            id="price-input"
                            onKeyPress={(event: any) =>
                                (event?.key === '-' || event?.key === '+') && event.preventDefault()
                            }
                        />
                    </InputGroup>
                ) : (
                    <p className="text-muted mb-0 mt-2 euro-symbol">{priceSplitter(Math.round(item?.price ?? 0))}</p>
                )}
            </td>
        </tr>
    );
};

export default SocialPlatformOfferItem;
